import axios from '@axios'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

marked.setOptions({ breaks: true, gfm: true })

export const mixinChat = {
  data() {
    return {
      userMessage: '',
      messages: [],
      loading: false,
      streaming: false,
      inputFocused: false,
      toolsUsed: [],
      loadingMessage: 'Procesando...',
      loadingMessages: [
        'Analizando tu consulta...',
        'Buscando información...',
        'Consultando el sistema...',
        'Procesando resultados...',
      ],
      loadingInterval: null,
      toolLabels: {
        search_orders: 'Pedidos',
        get_order_detail: 'Detalle de pedido',
        search_quotes: 'Cotizaciones',
        get_quote_detail: 'Detalle de cotización',
        search_products: 'Productos',
        check_stock: 'Stock',
        search_customers: 'Clientes',
        get_customer_detail: 'Detalle de cliente',
      },
      activeTools: [],
      toolErrors: [],
      streamInterrupted: false,
      lastUserMessage: '',
      doneReceived: false,
      welcomeData: null,
      feedbackGiven: {},
      chatEnabled: true,
      currentContext: null,
    }
  },

  computed: {
    toolsUsedLabels() {
      return this.toolsUsed.map(t => this.toolLabels[t] || t)
    },
  },

  async created() {
    await this.loadHistory()
    if (this.messages.length === 0) {
      await this.loadWelcome()
    }
  },

  methods: {
    async loadHistory() {
      try {
        const { data } = await axios.get('chat/history')
        if (data.res && data.data.messages && data.data.messages.length) {
          this.messages = data.data.messages
          this.feedbackGiven = {}
          this.$nextTick(() => this.scrollToBottom())
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[chat] loadHistory error:', e?.response?.status, e?.response?.data)
      }
    },

    async loadWelcome() {
      try {
        const { data } = await axios.get('chat/welcome')
        if (data.res) {
          this.welcomeData = data.data
          if (data.data.chat_enabled === false) {
            this.chatEnabled = false
          }
        }
      } catch (e) {
        // silencioso
      }
    },

    async saveHistory() {
      try {
        await axios.post('chat/history', { messages: this.messages })
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[chat] saveHistory error:', e?.response?.status, e?.response?.data)
      }
    },

    async clearHistory() {
      try {
        await axios.delete('chat/history')
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[chat] clearHistory error:', e?.response?.status, e?.response?.data)
      }
    },

    askQuestion(question) {
      if (question.endsWith(': ') || question.endsWith('#')) {
        this.userMessage = question
        this.$nextTick(() => {
          if (this.$refs.chatInput) {
            this.$refs.chatInput.focus()
          }
        })
        return
      }
      this.userMessage = question
      this.sendMessage()
    },

    async sendMessage() {
      const text = this.userMessage.trim()
      if (!text || this.loading) return

      this.lastUserMessage = text
      this.streamInterrupted = false
      this.doneReceived = false
      this.messages.push({ role: 'user', content: text })
      this.userMessage = ''
      this.loading = true
      this.streaming = false
      this.toolsUsed = []
      this.scrollToBottom()

      const history = this.messages.slice(0, -1).map(m => ({
        role: m.role,
        content: m.content,
      }))

      let msgIndex = 0;
      [this.loadingMessage] = this.loadingMessages
      this.loadingInterval = setInterval(() => {
        msgIndex = (msgIndex + 1) % this.loadingMessages.length
        this.loadingMessage = this.loadingMessages[msgIndex]
      }, 2000)

      try {
        const payload = {
          message: text,
          history,
        }
        if (this.currentContext) {
          payload.context = this.currentContext
        }

        const baseUrl = axios.defaults.baseURL || ''
        const token = localStorage.getItem('DuttonAuthToken')

        const response = await fetch(`${baseUrl}chat/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            Accept: 'text/event-stream',
          },
          body: JSON.stringify(payload),
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }

        this.messages.push({ role: 'assistant', content: '' })
        const assistantIndex = this.messages.length - 1

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop()

          let eventType = ''
          lines.forEach(line => {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim()
            } else if (line.startsWith('data: ')) {
              const dataStr = line.slice(6)
              try {
                const parsed = JSON.parse(dataStr)
                if (eventType === 'delta' && parsed.content) {
                  if (!this.streaming) {
                    this.streaming = true
                    clearInterval(this.loadingInterval)
                    this.loadingInterval = null
                  }
                  this.messages[assistantIndex].content += parsed.content
                  this.$set(this.messages, assistantIndex, { ...this.messages[assistantIndex] })
                  this.scrollToBottom()
                } else if (eventType === 'tool_start') {
                  const label = this.toolLabels[parsed.tool] || parsed.tool
                  this.activeTools = [...this.activeTools, parsed.tool]
                  this.loadingMessage = `Consultando ${label}...`
                } else if (eventType === 'tool_end') {
                  this.activeTools = this.activeTools.filter(t => t !== parsed.tool)
                  if (parsed.has_error) {
                    this.toolErrors = [...this.toolErrors, parsed.tool]
                  }
                } else if (eventType === 'done') {
                  this.doneReceived = true
                  this.toolsUsed = parsed.tools_used || []
                  this.toolErrors = parsed.tool_errors || this.toolErrors
                } else if (eventType === 'error') {
                  this.messages[assistantIndex].content = parsed.message || 'Error del asistente'
                  this.$set(this.messages, assistantIndex, { ...this.messages[assistantIndex] })
                }
              } catch (e) {
                // Ignorar líneas JSON inválidas
              }
              eventType = ''
            }
          })
        }

        // Detect if stream ended without done event
        if (!this.doneReceived) {
          this.streamInterrupted = true
        }

        // Force re-render with linkification now that streaming is done
        this.streaming = false
        const lastMsg = this.messages[assistantIndex]
        if (lastMsg) {
          this.$set(this.messages, assistantIndex, { ...lastMsg })
        }

        if (document.hidden) {
          this.playNotificationSound()
        }

        await this.saveHistory()
      } catch (error) {
        const lastMsg = this.messages[this.messages.length - 1]
        if (lastMsg && lastMsg.role === 'assistant' && !lastMsg.content) {
          this.$set(this.messages, this.messages.length - 1, {
            role: 'assistant',
            content: 'Error de conexión. Intenta de nuevo.',
          })
        } else if (!lastMsg || lastMsg.role !== 'assistant') {
          this.messages.push({
            role: 'assistant',
            content: 'Error de conexión. Intenta de nuevo.',
          })
        }
        if (this.showErrors) {
          this.showErrors(error)
        }
      } finally {
        clearInterval(this.loadingInterval)
        this.loadingInterval = null
        this.loading = false
        this.streaming = false
        this.activeTools = []
        this.scrollToBottom()
      }
    },

    async clearChat() {
      this.messages = []
      this.toolsUsed = []
      this.toolErrors = []
      this.activeTools = []
      this.streamInterrupted = false
      this.feedbackGiven = {}
      await this.clearHistory()
    },

    retryLastMessage() {
      if (!this.lastUserMessage) return
      this.streamInterrupted = false
      // Remove the last (interrupted) assistant message
      const lastMsg = this.messages[this.messages.length - 1]
      if (lastMsg && lastMsg.role === 'assistant') {
        this.messages.splice(this.messages.length - 1, 1)
      }
      this.userMessage = this.lastUserMessage
      this.sendMessage()
    },

    async submitFeedback(messageIndex, feedback) {
      const current = this.feedbackGiven[messageIndex]
      if (current === feedback) return

      this.$set(this.feedbackGiven, messageIndex, feedback)

      try {
        const msg = this.messages[messageIndex]
        await axios.post('chat/feedback', {
          message_index: messageIndex,
          feedback,
          message_content: msg ? msg.content : null,
        })
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[chat] feedback error:', e?.response?.status)
      }
    },

    setContext(type, id, label) {
      this.currentContext = { type, id, label }
    },

    clearContext() {
      this.currentContext = null
    },

    escapeHtml(text) {
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
    },

    formatMessage(text, isStreaming = false) {
      if (!text) return ''
      if (isStreaming) return `<span style="white-space:pre-wrap">${this.escapeHtml(text)}</span>`
      return this.linkifyEntities(DOMPurify.sanitize(marked(text), { ADD_ATTR: ['data-route', 'data-id'] }))
    },

    linkifyEntities(rawHtml) {
      let result = rawHtml.replace(
        /[Pp]edido\s*#\s*(\d+)/g,
        '<a class="chat-entity-link" data-route="sales-customer-orders" data-id="$1" href="#">Pedido #$1</a>',
      )
      result = result.replace(
        /[Cc]otizaci[oó]n\s*#\s*(\d+)/g,
        '<a class="chat-entity-link" data-route="sales-customer-quotes" data-id="$1" href="#">Cotización #$1</a>',
      )
      result = result.replace(
        /[Cc]liente.*?ID[:\s]+(\d+)/g,
        match => {
          const [, entityId] = match.match(/ID[:\s]+(\d+)/)
          return `<a class="chat-entity-link" data-route="contacts-customers" data-id="${entityId}" href="#">${match}</a>`
        },
      )
      return result
    },

    handleEntityClick(e) {
      const link = e.target.closest('.chat-entity-link')
      if (!link) return
      e.preventDefault()
      const { route, id } = link.dataset
      if (route && this.$router) {
        // Marcar que el chat debe permanecer abierto tras la navegación
        localStorage.setItem('dutton_chat_keep_open', 'true')
        this.$router.push({ name: route, params: { id } }).catch(() => {})
      }
    },

    playNotificationSound() {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext
        if (!AudioCtx) return
        const ctx = new AudioCtx()
        const oscillator = ctx.createOscillator()
        const gainNode = ctx.createGain()
        oscillator.connect(gainNode)
        gainNode.connect(ctx.destination)
        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(800, ctx.currentTime)
        oscillator.frequency.exponentialRampToValueAtTime(550, ctx.currentTime + 0.15)
        gainNode.gain.setValueAtTime(0.25, ctx.currentTime)
        gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4)
        oscillator.start(ctx.currentTime)
        oscillator.stop(ctx.currentTime + 0.4)
      } catch (e) {
        // silencioso si el browser no soporta Web Audio API
      }
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const body = this.$refs.chatBody
        if (body) {
          body.scrollTop = body.scrollHeight
        }
      })
    },

    copyMessage(text) {
      const plainText = text
        .replace(/<[^>]+>/g, '')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
      navigator.clipboard.writeText(plainText).then(() => {
        this.makeToast('success', 'Copiado', 'Mensaje copiado al portapapeles')
      }).catch(() => {
        this.makeToast('danger', 'Error', 'No se pudo copiar el mensaje')
      })
    },
  },
}

export const _ = null
